import { downloadAuthedFile, fetcher } from "@/lib/api";
import { trackChokh } from "@/lib/chokh";
import { cfImageUrl, type CfImageVariant } from "@/lib/cloudflare-image";
import { isNoActiveSessionRefusal } from "@/features/quiz/lib/active-session-probe";
import { QUIZ_COVER_GRADIENTS } from "@/features/quiz/lib/quiz-theme";
import type { ProctorConfig } from "@/features/proctor";
import type {
  ExamRoom,
  ExamRoomResetResult,
  ExamRoomStartResult,
  QuizCallerNetwork,
  QuizSebPanel,
  QuizLobby,
  QuizLobbyAdmission,
  QuizStartResult,
  QuizPublic,
  QuizCatalogParams,
  QuizCatalogResponse,
  MyQuizSessionsResponse,
  MyQuizHistoryResponse,
  QuizDifficulty,
  QuizAssessmentType,
  QuizLanguage,
  QuizSession,
  QuizHeartbeatResult,
  QuizNavigation,
  QuizNextQuestionResult,
  QuizSessionSettings,
  QuizPlayerSessionMode,
  QuizSection,
  QuizQuestion,
  QuizAnswerResponse,
  SaveQuizAnswerResult,
  SaveQuizAnswersBatchResult,
  QuizResult,
  CreatorQuizListResponse,
  CreatorQuizListSort,
  CreatorQuizSummary,
  CreatorQuizDetail,
  CreatorQuestion,
  CreatorSection,
  QuizEmbedConfig,
  QuizFormBasicInfo,
  CreatorSessionsResponse,
  CreatorSessionDetailResult,
  QuizAnalyticsData,
  QuizGradeInput,
  ManualGradeResult,
  TemplateGalleryParams,
  TemplateGalleryResponse,
  QuizTemplateDetail,
  AiQuestionGenerateParams,
  QuizEmbedPayload,
  QuizEmbedUiConfig,
  LeaderboardEntry,
  LeaderboardResponse,
  QuizLeaderboardConfig,
  ExamRosterResponse,
  ExamRosterImportResponse,
  ExamRosterEntryView,
  ExamJoinResponse,
  CodingMarkOverrideResult,
  CodePlaybackView,
  ExamClusterBoard,
  ExamSimilarityPair,
  ExamAiTrapBoard,
  LeakFinderOverview,
  LeakFinderResult,
  ExamAnswerSimilarityBoard,
  ExamAnswerPair,
  IntegrityBoard,
  IntegrityDecision,
  IntegrityOutcome,
  IntegrityReport,
  StudentIntegrityDecision,
  InvigilationBoard,
  InvigilationBroadcastResult,
  InvigilationExtraTimeResult,
  InvigilationForceSubmitResult,
  InvigilationSort,
  InvigilationStudentPanel,
  ExamQuestionReplyResult,
  ExamQuestionThread,
  MonitorExamQuestions,
  PhotoCreditSummary,
  QuizSittingPhoto,
  SittingEvidence,
  PhonePairingStatus,
  PhonePairingTicket,
} from "./types";
import { getApiBaseUrl } from "@/lib/api-base-url";
import type { ShareSurface } from "@/features/quiz/lib/screen-share";

// ── Helpers ────────────────────────────────────────────────────────────────

export function getQuizCoverUrl(
  imageId: string | undefined,
  variant: CfImageVariant = "card",
): string | null {
  if (!imageId) return null;
  const url = cfImageUrl(imageId, variant);
  return url || null;
}

// ── Labels ─────────────────────────────────────────────────────────────────

export const DIFFICULTY_LABELS: Record<QuizDifficulty, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  mixed: "Mixed",
};

export const ASSESSMENT_TYPE_LABELS: Record<QuizAssessmentType, string> = {
  quiz: "Quiz",
  exam: "Exam",
  practice: "Practice",
  survey: "Survey",
};

export const LANGUAGE_LABELS: Record<QuizLanguage, string> = {
  en: "English",
  bn: "Bengali",
  bilingual: "Bilingual",
};

export const DIFFICULTY_COLORS: Record<QuizDifficulty, string> = {
  easy: "bg-emerald-100 text-emerald-700",
  medium: "bg-amber-100 text-amber-700",
  hard: "bg-red-100 text-red-700",
  mixed: "bg-purple-100 text-purple-700",
};

export const ASSESSMENT_TYPE_COLORS: Record<QuizAssessmentType, string> = {
  quiz: "bg-blue-100 text-blue-700",
  exam: "bg-red-100 text-red-700",
  practice: "bg-teal-100 text-teal-700",
  survey: "bg-cyan-100 text-cyan-800",
};

/** Gradient placeholder for quizzes without a cover image (`QZ-BLD05`: one map with the builder's preview). */
export const DIFFICULTY_GRADIENTS: Record<QuizDifficulty, string> = QUIZ_COVER_GRADIENTS;

// ── API functions ──────────────────────────────────────────────────────────

export async function fetchQuizCatalog(
  params: QuizCatalogParams,
): Promise<QuizCatalogResponse> {
  const q = new URLSearchParams();
  if (params.category) q.set("category", params.category);
  if (params.difficulty) q.set("difficulty", params.difficulty);
  if (params.language) q.set("language", params.language);
  if (params.assessmentType) q.set("assessmentType", params.assessmentType);
  if (params.search) q.set("search", params.search);
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  const res = await fetcher<QuizCatalogResponse>(
    `/v1/quizzes${qs ? `?${qs}` : ""}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

export async function fetchQuizBySlug(slug: string): Promise<QuizPublic> {
  const res = await fetcher<{ quiz: QuizPublic }>(`/v1/quizzes/${slug}`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  const q = res.data.quiz;
  const questionCount = typeof q.questionCount === "number" && Number.isFinite(q.questionCount) ? q.questionCount : 0;
  return { ...q, questionCount };
}

/** GET /v1/quiz-platform/quizzes/:slug/embed — iframe-safe full player payload (Referer sent by browser). */
export async function fetchEmbedQuiz(slug: string): Promise<QuizEmbedPayload> {
  const res = await fetcher<{ quiz: Record<string, unknown> }>(
    `/v1/quiz-platform/quizzes/${encodeURIComponent(slug)}/embed`,
  );
  if (!res.success) {
    throw new Error((res as { error?: { message?: string } }).error?.message ?? "Failed to load embed");
  }
  const raw = res.data.quiz;
  const qs = raw["templateSnapshot"] as { questions?: QuizQuestion[] } | undefined;
  const n = qs?.questions?.length ?? (raw["questionCount"] as number | undefined) ?? 0;
  const embed = raw["embedConfig"] as QuizEmbedUiConfig;
  return {
    ...(raw as Omit<QuizEmbedPayload, "questionCount" | "embedConfig">),
    questionCount: typeof raw["questionCount"] === "number" ? raw["questionCount"] : n,
    embedConfig: embed,
  } as QuizEmbedPayload;
}

export async function fetchQuizByAccessCode(
  accessCode: string,
): Promise<QuizPublic> {
  const res = await fetcher<{ quiz: QuizPublic }>(
    `/v1/quizzes/code/${accessCode.toUpperCase()}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.quiz;
}

/**
 * `GET /v1/me/quiz-sessions` names an attempt `sessionId` and its quiz `quizId`
 * (`getMySessions`), while every client type in this app calls them `_id` and
 * `standaloneQuizId`. Both readers of this route go through here so the rename
 * happens in one place.
 *
 * It is written as a shared helper rather than copied because the copy is what
 * broke: the quiz-scoped reader normalised, the dashboard history reader did
 * not, so "View Results" on `/dashboard/quizzes` linked to
 * `/results/undefined` and the results page answered "Failed to load results."
 */
function normalizeMySession<T extends { _id?: string; standaloneQuizId?: string }>(
  session: T,
  fallbackQuizId?: string,
): T & { _id: string; standaloneQuizId: string } {
  const raw = session as T & { sessionId?: string; quizId?: string };
  return {
    ...session,
    _id: session._id ?? raw.sessionId ?? "",
    standaloneQuizId: session.standaloneQuizId ?? raw.quizId ?? fallbackQuizId ?? "",
  };
}

export async function fetchMyQuizSessions(
  quizId: string,
): Promise<MyQuizSessionsResponse> {
  const res = await fetcher<MyQuizSessionsResponse>(
    `/v1/me/quiz-sessions?quizId=${quizId}&limit=5`,
  );
  if (!res.success) return { sessions: [], page: 1, limit: 5, total: 0 };
  return {
    ...res.data,
    sessions: res.data.sessions.map((session) => normalizeMySession(session, quizId)),
  };
}

// ── Quiz Player API (Phase 1B-ii) ──────────────────────────────────────────

/** Unified session shape returned by both start and resume endpoints. */
/** Exported for the Skill Test start (`LN-TST01`), which hands back the same player payload. */
export type SessionApiResponse = {
  session: {
    sessionId: string;
    status: string;
    startedAt: string;
    lastActiveAt: string;
    maxScore: number;
    remainingSeconds: number | null;
    /** `RV-46`: the grant already counted in `remainingSeconds`. */
    extraTimeSeconds?: number;
    /** `RV-63`: the policed events the sitting already holds. */
    policedViolationCount?: number;
    answers: Array<{
      questionId: string;
      questionType: string;
      response: unknown;
      timeSpentMs: number;
      answeredAt: string;
    }>;
  };
  questions?: QuizQuestion[];
  sections?: QuizSection[];
  settings: Omit<QuizSessionSettings, "mode"> & { mode?: QuizPlayerSessionMode };
  isResume?: boolean;
  /** `EX-SEC07`: present only on a one-way sitting. */
  navigation?: QuizNavigation;
  /** `EX-PP01`: the sitting's photo proctoring; absent from an older server. */
  photo?: QuizSittingPhoto | null;
};

export function mapSessionApiResponse(quizId: string, data: SessionApiResponse): QuizSession {
  const { session, questions: rawQuestions, sections, settings, navigation, photo } = data;
  const questions = rawQuestions ?? [];
  const sessionSettings: QuizSession["sessionSettings"] = {
    ...settings,
    mode: settings.mode ?? "exam",
  };
  return {
    _id: session.sessionId,
    standaloneQuizId: quizId,
    status: session.status as QuizSession["status"],
    startedAt: session.startedAt,
    lastActiveAt: session.lastActiveAt,
    questionOrder: questions.map((q) => q._id),
    questions,
    answers: session.answers.map((a) => ({
      questionId: a.questionId,
      questionType: a.questionType as QuizSession["answers"][number]["questionType"],
      response: a.response as QuizSession["answers"][number]["response"],
      answeredAt: a.answeredAt,
    })),
    maxScore: session.maxScore,
    gradingStatus: "pending_review",
    remainingSeconds: session.remainingSeconds,
    // `RV-61`: the instant the server's seconds were true at, on this clock.
    receivedAt: Date.now(),
    ...(session.extraTimeSeconds !== undefined
      ? { extraTimeSeconds: session.extraTimeSeconds }
      : {}),
    ...(session.policedViolationCount !== undefined
      ? { policedViolationCount: session.policedViolationCount }
      : {}),
    ...(sections ? { sections } : {}),
    sessionSettings,
    ...(navigation ? { navigation } : {}),
    ...(photo ? { photo } : {}),
  };
}

/** POST /v1/quizzes/:quizId/sessions — start a new attempt (idempotent: returns existing if in_progress) */
/**
 * POST /v1/quizzes/:quizId/sessions: a sitting, or (`EX-SEC03`) the lobby of an
 * exam that starts together, which carries no paper. `roomCode` is the code on
 * the room's screen, sent only when the quiz asks for it.
 */
export async function startQuizSitting(
  quizId: string,
  options: {
    password?: string;
    forceNew?: boolean;
    roomCode?: string;
    screenSurface?: ShareSurface;
    camera?: boolean;
    /** `EX-PP03`: the pairing whose phone is ready. */
    phonePairingId?: string;
  } = {},
): Promise<QuizStartResult> {
  const body: Record<string, unknown> = {};
  if (options.password) body.password = options.password;
  if (options.forceNew) body.forceNew = true;
  if (options.roomCode) body.roomCode = options.roomCode;
  // `EX-PP01`: what the page shares, for an exam that takes pictures.
  if (options.screenSurface) body.screenShare = { surface: options.screenSurface };
  // `EX-PP02`: the camera runs, for an exam that takes webcam photos.
  if (options.camera) body.camera = { on: true };
  // `EX-PP03`: the paired phone, for an exam that uses the phone camera.
  if (options.phonePairingId) body.phone = { pairingId: options.phonePairingId };
  const res = await fetcher<SessionApiResponse | { lobby: QuizLobbyAdmission }>(
    `/v1/quizzes/${quizId}/sessions`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  if ("lobby" in res.data) return { kind: "lobby", lobby: res.data.lobby };
  // Here rather than in the players: the attempt page, the embed and the resume
  // path all come through this one call (`AN-INT01`).
  trackChokh("quiz_start", { quizId });
  return { kind: "session", session: mapSessionApiResponse(quizId, res.data) };
}

/** A start for a player that has no lobby (the embed: an exam with a room setting is never embedded). */
export async function startQuizSession(
  quizId: string,
  password?: string,
  forceNew?: boolean,
): Promise<QuizSession> {
  const started = await startQuizSitting(quizId, { password, forceNew });
  if (started.kind === "lobby") throw new Error("This exam starts when the invigilator starts it.");
  return started.session;
}

/** GET /v1/quiz-platform/my-network (`EX-SEC08`): the caller's own address, for "Add the network I'm on now". */
export async function fetchMyNetwork(): Promise<QuizCallerNetwork> {
  const res = await fetcher<QuizCallerNetwork>("/v1/quiz-platform/my-network");
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-platform/quizzes/:id/seb (`EX-SEC12`): the builder's "For the lab" panel. */
export async function fetchSebPanel(quizId: string): Promise<QuizSebPanel> {
  const res = await fetcher<QuizSebPanel>(`/v1/quiz-platform/quizzes/${quizId}/seb`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-platform/quizzes/:id/seb/file (`EX-SEC12`): "Download the exam file (.seb)". */
export async function downloadSebFile(quizId: string, fallbackFilename: string): Promise<void> {
  await downloadAuthedFile(`/v1/quiz-platform/quizzes/${quizId}/seb/file`, fallbackFilename);
}

/** POST /v1/quiz-platform/quizzes/:id/seb/new-file (`EX-SEC12`): a new quit password and link; earlier files stop working. */
export async function makeNewSebFile(quizId: string): Promise<QuizSebPanel> {
  const res = await fetcher<QuizSebPanel>(`/v1/quiz-platform/quizzes/${quizId}/seb/new-file`, { method: "POST" });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quizzes/:quizId/lobby (`EX-SEC03`): the waiting page's poll. */
export async function fetchQuizLobby(quizId: string): Promise<QuizLobby> {
  const res = await fetcher<QuizLobby>(`/v1/quizzes/${quizId}/lobby`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET /v1/quizzes/:quizId/sessions/active: fetch the active session for resume.
 *
 * `null` means "nothing to resume", which is what the API answers for every
 * learner who has not started yet. It answers it as a 404, so it arrives here
 * as a thrown `ApiError` and has to be classified rather than assumed: the
 * `if (!res.success)` guard this replaced could never fire, because `fetcher`
 * throws on an error envelope instead of returning one. Everything else still
 * throws, so a real failure can never be mistaken for "no attempt in progress"
 * and quietly offered a second session beside a live one.
 */
export async function fetchActiveSession(quizId: string): Promise<QuizSession | null> {
  try {
    const res = await fetcher<SessionApiResponse>(
      `/v1/quizzes/${quizId}/sessions/active`,
    );
    if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
    return mapSessionApiResponse(quizId, res.data);
  } catch (err) {
    if (isNoActiveSessionRefusal(err)) return null;
    throw err;
  }
}

/**
 * POST /v1/quiz-sessions/:sessionId/heartbeat — keep-alive ping.
 *
 * Also the release channel for a locked session: the response carries the
 * server-authoritative status, so a player flagged for violations learns that an
 * instructor unflagged it. The client never decides its own status.
 *
 * `telemetrySeq` attests how far the player's telemetry deliveries have settled
 * (`EX-INT02`, `docs/27` §5.3). The server compares it with what it actually
 * stored, so a violation endpoint blocked in devtools is reported by the very
 * request that keeps the session alive.
 */
export async function heartbeatQuizSession(
  sessionId: string,
  telemetrySeq?: number,
  /** `EX-PP01`: whether the page still shares the screen, on a sitting that takes pictures. */
  screenShared?: boolean,
  /** `EX-PP02`: whether the page's camera still runs, on a sitting that takes webcam photos. */
  cameraOn?: boolean,
): Promise<QuizHeartbeatResult> {
  const res = await fetcher<QuizHeartbeatResult>(
    `/v1/quiz-sessions/${sessionId}/heartbeat`,
    {
      method: "POST",
      body: JSON.stringify({
        ...(telemetrySeq !== undefined ? { telemetrySeq } : {}),
        ...(screenShared !== undefined ? { screenShared } : {}),
        ...(cameraOn !== undefined ? { cameraOn } : {}),
      }),
    },
  );
  if (!res.success) return { ok: false, status: null, extraTimeSeconds: 0, notice: null };
  return {
    ok: res.data.ok,
    status: res.data.status ?? null,
    /**
     * The server's telemetry chain head (`EX-INT03`). The proctoring queue asks
     * for it before a retry so a batch the server already stored is dropped
     * rather than re-sent — otherwise a lost *response* costs the student a
     * duplicate record and a `seq_replay`.
     */
    telemetry: res.data.telemetry ?? null,
    /**
     * `EX-EXM05` — what an invigilator changed under a sitting student. Carried
     * on the beat that already runs rather than on a channel of its own, so a
     * broadcast to a room of fifty costs no extra request per student.
     */
    extraTimeSeconds: res.data.extraTimeSeconds ?? 0,
    notice: res.data.notice ?? null,
    /** `EX-SEC14`: the reply count; absent from an older server. */
    ...(res.data.invigilatorReplies !== undefined
      ? { invigilatorReplies: res.data.invigilatorReplies }
      : {}),
    /** `RV-61`: what the countdown re-anchors on; absent from an older server. */
    ...(res.data.remainingSeconds !== undefined
      ? { remainingSeconds: res.data.remainingSeconds }
      : {}),
    /** `RV-90`: where the counter restarts on a release; absent from an older server. */
    ...(res.data.policedViolationCount !== undefined
      ? { policedViolationCount: res.data.policedViolationCount }
      : {}),
    /** `EX-SEC07`: a one-way sitting's open question; absent from an older server. */
    ...(res.data.oneWay !== undefined ? { oneWay: res.data.oneWay } : {}),
  };
}

/**
 * POST /v1/quiz-sessions/:sessionId/next (`EX-SEC07`, `docs/27` §9.29): leave
 * the open question of a one-way sitting. `questionId` is the question being
 * left; when the server has already moved past it (its time ran out), it
 * answers where the student is now and moves nothing, so a retry never skips
 * a second question.
 */
export async function nextQuizQuestion(
  sessionId: string,
  questionId: string,
): Promise<QuizNextQuestionResult> {
  const res = await fetcher<QuizNextQuestionResult>(`/v1/quiz-sessions/${sessionId}/next`, {
    method: "POST",
    body: JSON.stringify({ questionId }),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** PATCH /v1/quiz-sessions/:sessionId/answer — autosave one answer */
export async function saveQuizAnswer(
  sessionId: string,
  questionId: string,
  questionType: string,
  response: QuizAnswerResponse,
): Promise<SaveQuizAnswerResult> {
  const res = await fetcher<SaveQuizAnswerResult>(
    `/v1/quiz-sessions/${sessionId}/answer`,
    {
      method: "PATCH",
      body: JSON.stringify({ questionId, questionType, response }),
    },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

export type QuizAnswerBatchItem = {
  questionId: string;
  questionType: string;
  response: QuizAnswerResponse;
  timeSpentMs?: number;
};

/** PATCH /v1/quiz-sessions/:sessionId/answers — batch autosave */
export async function saveQuizAnswersBatch(
  sessionId: string,
  answers: QuizAnswerBatchItem[],
): Promise<SaveQuizAnswersBatchResult> {
  const res = await fetcher<SaveQuizAnswersBatchResult>(
    `/v1/quiz-sessions/${sessionId}/answers`,
    {
      method: "PATCH",
      body: JSON.stringify({
        answers: answers.map((a) => ({
          questionId: a.questionId,
          questionType: a.questionType,
          response: a.response,
          timeSpentMs: a.timeSpentMs ?? 0,
        })),
      }),
    },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * POST /v1/quiz-sessions/:sessionId/submit: submit attempt.
 *
 * `answers` are the ones the autosave has not landed yet (`RV-05`): the server
 * merges them over the autosaved answers before it grades, so an answer typed
 * inside the autosave's debounce, or still in flight, is graded rather than
 * read as missing. None to carry sends `{}`.
 */
export async function submitQuizSession(
  sessionId: string,
  answers: QuizAnswerBatchItem[] = [],
): Promise<{ sessionId: string }> {
  // Backend validates JSON body with Zod; `undefined` fails safeParse — send `{}`.
  const body =
    answers.length > 0
      ? {
          answers: answers.map((a) => ({
            questionId: a.questionId,
            questionType: a.questionType,
            response: a.response,
            timeSpentMs: a.timeSpentMs ?? 0,
          })),
        }
      : {};
  const res = await fetcher<{ sessionId: string }>(
    `/v1/quiz-sessions/${sessionId}/submit`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  trackChokh("quiz_submit");
  return res.data;
}

/**
 * POST /v1/quiz-sessions/:sessionId/violation — log one telemetry event.
 *
 * Carries the event's monotonic `seq` and the previous event's server-issued
 * hash (`EX-INT02`, `docs/27` §5.3) and returns the new chain head, which the
 * next event presents as its `prevHash`. The client never computes a digest —
 * the server owns the hash and hands back a receipt.
 */
export async function logQuizViolation(
  sessionId: string,
  event: {
    type: string;
    detail?: string;
    questionId?: string;
    timestamp?: string;
    seq?: number;
    prevHash?: string | null;
  },
): Promise<{ seq: number; hash: string | null } | null> {
  const res = await fetcher<{ telemetry?: { seq: number; hash: string | null } }>(
    `/v1/quiz-sessions/${sessionId}/violation`,
    {
      method: "POST",
      body: JSON.stringify(event),
    },
  );
  if (!res.success) return null;
  return res.data.telemetry ?? null;
}

/**
 * The wire shape of one queued telemetry event.
 *
 * Deliberately structural rather than importing `ProctorEvent`: this file is the
 * quiz's API layer, and the proctoring module stays a module the quiz *adapts*
 * to rather than one it is fused with.
 */
type QuizTelemetryEvent = {
  type: string;
  timestamp: string;
  detail?: string;
  itemId?: string;
  seq: number;
  prevHash: string | null;
  paste?: { bytes: number; lines: number };
};

/** The route's field names. `itemId` is the proctoring module's word for `questionId`. */
function toWireEvent(event: QuizTelemetryEvent) {
  return {
    type: event.type,
    detail: event.detail,
    questionId: event.itemId,
    timestamp: event.timestamp,
    seq: event.seq,
    prevHash: event.prevHash,
    paste: event.paste,
  };
}

/**
 * Fire the batch without waiting for, or reading, the answer.
 *
 * `keepalive` is what lets a request outlive the document that started it, which
 * is the entire requirement of a `pagehide` flush. It is used instead of
 * `navigator.sendBeacon` because a beacon cannot carry an `Authorization`
 * header and this API accepts no other credential — a beacon here would be
 * answered 401 and the evidence discarded, which is precisely what the durable
 * queue exists to prevent.
 */
export function quizTelemetryBatchRequest(
  sessionId: string,
  events: readonly QuizTelemetryEvent[],
  opts: { keepalive?: boolean } = {},
): void {
  if (events.length === 0) return;
  void fetcher(`/v1/quiz-sessions/${sessionId}/violations`, {
    method: "POST",
    body: JSON.stringify({ events: events.map(toWireEvent) }),
    ...(opts.keepalive ? { keepalive: true } : {}),
  }).catch(() => {
    // Unacknowledged by construction. The rows stay in IndexedDB and the next
    // page load reconciles them against the server's chain head.
  });
}

/**
 * POST /v1/quiz-sessions/:sessionId/violations — deliver an **ordered** batch.
 *
 * One request for the whole queue, so the chain's order is explicit in the
 * payload rather than an accident of request scheduling, and so a reconnect
 * backlog is not refused event-by-event by the per-session rate limit. Returns
 * the chain head after the last event, which the next batch presents as its
 * `prevHash`.
 */
export async function logQuizViolationBatch(
  sessionId: string,
  events: readonly QuizTelemetryEvent[],
): Promise<{ seq: number; hash: string | null } | null> {
  if (events.length === 0) return null;
  const res = await fetcher<{ telemetry?: { seq: number; hash: string | null } }>(
    `/v1/quiz-sessions/${sessionId}/violations`,
    {
      method: "POST",
      body: JSON.stringify({ events: events.map(toWireEvent) }),
    },
  );
  if (!res.success) return null;
  return res.data.telemetry ?? null;
}

/** GET /v1/quiz-sessions/:sessionId/result — get graded result */
export async function fetchQuizResult(sessionId: string): Promise<QuizResult> {
  const res = await fetcher<QuizResult>(
    `/v1/quiz-sessions/${sessionId}/result?includeGrades=true`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return {
    ...res.data,
    questionReview: res.data.questionReview ?? null,
  };
}

// ── Student My Quizzes history ─────────────────────────────────────────────

/** GET /v1/me/quiz-sessions — paginated attempt history */
export async function fetchMyQuizHistory(params: {
  page?: number;
  limit?: number;
  status?: string;
}): Promise<MyQuizHistoryResponse> {
  const q = new URLSearchParams();
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  if (params.status) q.set("status", params.status);
  const qs = q.toString();
  const res = await fetcher<MyQuizHistoryResponse>(
    `/v1/me/quiz-sessions${qs ? `?${qs}` : ""}`,
  );
  if (!res.success) return { sessions: [], page: 1, limit: 10, total: 0 };
  return {
    ...res.data,
    sessions: res.data.sessions.map((session) => normalizeMySession(session)),
  };
}

// ── Creator CRUD ────────────────────────────────────────────────────────────

/** GET /v1/quiz-platform/quizzes — creator's quiz list */
export async function fetchCreatorQuizList(params: {
  page?: number;
  limit?: number;
  status?: string;
  /** `QZ-DSH02`: the list's search, kind and sort; absent keeps the old list. */
  search?: string;
  assessmentType?: string;
  sort?: CreatorQuizListSort;
}): Promise<CreatorQuizListResponse> {
  const q = new URLSearchParams();
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  if (params.status) q.set("status", params.status);
  if (params.search?.trim()) q.set("search", params.search.trim());
  if (params.assessmentType) q.set("assessmentType", params.assessmentType);
  if (params.sort) q.set("sort", params.sort);
  const qs = q.toString();
  const res = await fetcher<CreatorQuizListResponse>(
    `/v1/quiz-platform/quizzes${qs ? `?${qs}` : ""}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  // The list derives `questionCount` since 2026-09-18; a backend from before
  // that sends no count at all, and an ICU plural over `undefined` printed
  // "NaN questions" on every row. Zero is the honest reading of "not sent".
  return {
    ...res.data,
    quizzes: res.data.quizzes.map((q) => ({
      ...q,
      questionCount:
        typeof q.questionCount === "number" && Number.isFinite(q.questionCount)
          ? q.questionCount
          : 0,
    })),
  };
}

/** GET /v1/quiz-platform/quizzes/:id — creator quiz detail with questions */
export async function fetchCreatorQuizDetail(id: string): Promise<CreatorQuizDetail> {
  const res = await fetcher<{ quiz: CreatorQuizDetail }>(
    `/v1/quiz-platform/quizzes/${id}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.quiz;
}

type QuizPayload = Partial<QuizFormBasicInfo> & {
  coverImageId?: string;
  themeConfig?: { primaryColor?: string; coverStyle: string };
  settings?: Partial<{
    timeLimitSeconds: number | null;
    passingPercentage: number;
    maxAttempts: number;
    shuffleQuestions: boolean;
    shuffleOptions: boolean;
    showCorrectAnswers: string;
    showExplanations: string;
    resultReleaseMode: string;
    allowReview: boolean;
    certificateEnabled: boolean;
  }>;
  scheduling?: {
    startAt?: string | null;
    endAt?: string | null;
    timezone: string;
  };
  templateSnapshot?: {
    sourceQuizTemplateId?: string;
    questions?: CreatorQuestion[];
  };
  sections?: CreatorSection[];
  password?: string;
  invitedEmails?: string[];
  /**
   * Whole-object replace — an omitted axis resolves to its default, never to the
   * stored value. The server resolves, refines, and relabels `level`.
   */
  proctorConfig?: Partial<ProctorConfig>;
  embedConfig?: QuizEmbedConfig;
  leaderboard?: QuizLeaderboardConfig;
  /**
   * `EX-ORG01`: sent on create **only** when the server has asked which
   * organization — an author in exactly one has it derived server-side, and for
   * everyone else it is meaningless. It is never a free choice: the server
   * refuses an organization the actor does not belong to.
   */
  workspaceId?: string;
  /**
   * Acknowledges a `LIVE_SESSIONS_CONFIRM_REQUIRED` refusal (409): the author
   * saw how many students are mid-exam and still wants the paper changed.
   */
  confirmLiveSessions?: true;
};

type LeaderboardApiBlock = {
  entries: LeaderboardEntry[];
  page: number;
  limit: number;
  total: number;
  totalParticipants: number;
  pages: number;
};

function mapLeaderboardResponse(
  L: LeaderboardApiBlock,
  myRank: { rank: number | null; totalParticipants: number } | null | undefined,
  released?: boolean,
): LeaderboardResponse {
  const rankInfo = myRank
    ? { rank: myRank.rank, totalParticipants: myRank.totalParticipants }
    : null;

  let userRank: LeaderboardEntry | null | undefined;
  if (myRank && myRank.rank != null) {
    userRank =
      L.entries.find((e) => e.rank === myRank.rank) ??
      ({
        rank: myRank.rank,
        displayName: "",
        score: null,
        percentage: null,
        timeSeconds: 0,
        attemptDate: "",
      } satisfies LeaderboardEntry);
  } else if (myRank && myRank.rank === null) {
    userRank = null;
  }

  return {
    entries: L.entries,
    totalParticipants: L.totalParticipants,
    page: L.page,
    limit: L.limit,
    total: L.total,
    pages: L.pages,
    rankInfo,
    userRank,
    ...(released !== undefined ? { released } : {}),
  };
}

/** GET /v1/quiz-platform/quizzes/:slug/leaderboard — public leaderboard (+ optional myRank when authed). */
export async function fetchQuizLeaderboard(
  slug: string,
  page = 1,
  limit = 50,
): Promise<LeaderboardResponse> {
  const q = new URLSearchParams();
  q.set("page", String(page));
  q.set("limit", String(limit));
  const res = await fetcher<{
    leaderboard: LeaderboardApiBlock;
    myRank: { rank: number | null; totalParticipants: number } | null;
    released?: boolean;
  }>(`/v1/quiz-platform/quizzes/${encodeURIComponent(slug)}/leaderboard?${q.toString()}`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return mapLeaderboardResponse(res.data.leaderboard, res.data.myRank, res.data.released);
}

/** GET /v1/quiz-platform/quizzes/:id/leaderboard/creator — creator (full names). */
export async function fetchCreatorLeaderboard(
  quizId: string,
  page = 1,
  limit = 50,
): Promise<LeaderboardResponse> {
  const q = new URLSearchParams();
  q.set("page", String(page));
  q.set("limit", String(limit));
  const res = await fetcher<{ leaderboard: LeaderboardApiBlock }>(
    `/v1/quiz-platform/quizzes/${quizId}/leaderboard/creator?${q.toString()}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return mapLeaderboardResponse(res.data.leaderboard, null);
}

/** POST /v1/quiz-platform/quizzes — create a new quiz */
export async function createStandaloneQuiz(
  payload: QuizPayload,
): Promise<{ quizId: string }> {
  const res = await fetcher<{ quiz: { _id: string } }>(
    `/v1/quiz-platform/quizzes`,
    { method: "POST", body: JSON.stringify(payload) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return { quizId: res.data.quiz._id };
}

/** PATCH /v1/quiz-platform/quizzes/:id — update quiz */
export async function updateStandaloneQuiz(
  id: string,
  payload: QuizPayload,
): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/quizzes/${id}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/**
 * DELETE /v1/quiz-platform/quizzes/:id/draft: drop a live quiz's unpublished
 * changes (`RV-50`). Answers the quiz as students sit it, with no draft.
 */
export async function discardQuizDraft(id: string): Promise<CreatorQuizDetail> {
  const res = await fetcher<{ quiz: CreatorQuizDetail }>(`/v1/quiz-platform/quizzes/${id}/draft`, {
    method: "DELETE",
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.quiz;
}

/**
 * POST /v1/quiz-platform/quizzes/:id/publish — publish quiz.
 *
 * When students are sitting the quiz the server refuses with
 * `LIVE_SESSIONS_CONFIRM_REQUIRED` (409, `details.liveSessionCount`) until the
 * author acknowledges; re-send with `confirmLiveSessions` after they confirm.
 */
export async function publishStandaloneQuiz(
  id: string,
  opts?: { confirmLiveSessions?: boolean },
): Promise<void> {
  const res = await fetcher(`/v1/quiz-platform/quizzes/${id}/publish`, {
    method: "POST",
    ...(opts?.confirmLiveSessions
      ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ confirmLiveSessions: true }),
        }
      : {}),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/** POST /v1/quiz-platform/quizzes/:id/close — close quiz */
export async function closeStandaloneQuiz(id: string): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/quizzes/${id}/close`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/** `QZ-DSH02`: GET /v1/quiz-platform/quizzes/summary, the Studio list's strip. */
export async function fetchCreatorQuizSummary(): Promise<CreatorQuizSummary> {
  const res = await fetcher<CreatorQuizSummary>("/v1/quiz-platform/quizzes/summary");
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** `QZ-LST01`: POST /v1/quiz-platform/quizzes/:id/restore, an archived quiz back as a draft, every result kept. */
export async function restoreStandaloneQuiz(id: string): Promise<void> {
  const res = await fetcher(`/v1/quiz-platform/quizzes/${id}/restore`, { method: "POST" });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/** DELETE /v1/quiz-platform/quizzes/:id: archives the quiz (no hard delete, the founder's decision খ১). */
export async function deleteStandaloneQuiz(id: string): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/quizzes/${id}`,
    { method: "DELETE" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/** POST /v1/quiz-platform/quizzes/:id/duplicate — duplicate quiz */
export async function duplicateStandaloneQuiz(id: string): Promise<{ quizId: string }> {
  const res = await fetcher<{ quiz: { _id: string } }>(
    `/v1/quiz-platform/quizzes/${id}/duplicate`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return { quizId: res.data.quiz._id };
}

function envelopeErrorMessage(res: unknown, fallback: string): string {
  if (res && typeof res === "object" && "error" in res) {
    const msg = (res as { error?: { message?: string } }).error?.message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return fallback;
}

/** `RV-78`: the key travels as the header the server reads first, and in the body. */
function idempotencyHeaders(key: string | undefined): { headers?: Record<string, string> } {
  return key ? { headers: { "Idempotency-Key": key } } : {};
}

export async function generateAiQuestions(
  payload: AiQuestionGenerateParams,
): Promise<CreatorQuestion[]> {
  const res = await fetcher<CreatorQuestion[]>(
    "/v1/quiz-platform/ai/generate",
    {
      method: "POST",
      ...idempotencyHeaders(payload.idempotencyKey),
      body: JSON.stringify(payload),
    },
  );
  if (!res.success) {
    throw new Error(
      envelopeErrorMessage(res, "AI generation failed. Please try again."),
    );
  }
  return res.data;
}

export async function generateAiQuestionsFromDoc(
  file: File,
  params: Omit<Extract<AiQuestionGenerateParams, { mode: "text" }>, "mode" | "text">,
): Promise<CreatorQuestion[]> {
  const form = new FormData();
  form.append("file", file);
  form.append("questionCount", String(params.questionCount));
  form.append("difficulty", params.difficulty);
  form.append("questionTypes", JSON.stringify(params.questionTypes));
  form.append("language", params.language);
  if (params.standaloneQuizId) {
    form.append("standaloneQuizId", params.standaloneQuizId);
  }
  if (params.idempotencyKey) {
    form.append("idempotencyKey", params.idempotencyKey);
  }

  const res = await fetcher<CreatorQuestion[]>(
    "/v1/quiz-platform/ai/generate-from-document",
    { method: "POST", ...idempotencyHeaders(params.idempotencyKey), body: form },
  );
  if (!res.success) {
    throw new Error(
      envelopeErrorMessage(res, "AI generation failed. Please try again."),
    );
  }
  return res.data;
}

// ── Creator sessions ────────────────────────────────────────────────────────

/** GET /v1/quiz-platform/quizzes/:id/sessions — all sessions for a quiz */
export async function fetchCreatorSessions(
  quizId: string,
  params: { page?: number; limit?: number; flaggedOnly?: boolean; gradingStatus?: string },
): Promise<CreatorSessionsResponse> {
  const q = new URLSearchParams();
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  if (params.flaggedOnly) q.set("flaggedOnly", "true");
  if (params.gradingStatus) q.set("gradingStatus", params.gradingStatus);
  const qs = q.toString();
  const res = await fetcher<CreatorSessionsResponse>(
    `/v1/quiz-platform/quizzes/${quizId}/sessions${qs ? `?${qs}` : ""}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-platform/sessions/:sessionId — session detail for teacher review */
export async function fetchCreatorSessionDetail(
  sessionId: string,
): Promise<CreatorSessionDetailResult> {
  const res = await fetcher<{ result: CreatorSessionDetailResult }>(
    `/v1/quiz-platform/sessions/${sessionId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.result;
}

/** PATCH /v1/quiz-platform/sessions/:sessionId/unflag — teacher unflag session */
export async function unflagQuizSession(sessionId: string): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/sessions/${sessionId}/unflag`,
    { method: "PATCH" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/**
 * POST /v1/quiz-platform/quizzes/:id/release-results — release results to all students.
 *
 * `RV-43`: while students are still sitting the server refuses with
 * `LIVE_SESSIONS_CONFIRM_REQUIRED` (409, `details.liveSessionCount`) until the
 * creator confirms; re-send with `confirmLiveSessions` after they do.
 */
export async function releaseQuizResults(
  quizId: string,
  opts?: { confirmLiveSessions?: boolean },
): Promise<{ quizId: string; resultsReleasedAt: string; alreadyReleased: boolean }> {
  const res = await fetcher<{ quizId: string; resultsReleasedAt: string; alreadyReleased: boolean }>(
    `/v1/quiz-platform/quizzes/${quizId}/release-results`,
    {
      method: "POST",
      ...(opts?.confirmLiveSessions
        ? {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ confirmLiveSessions: true }),
          }
        : {}),
    },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** PATCH /v1/quiz-platform/sessions/:sessionId/grade — teacher submits manual grades */
export async function gradeQuizSession(
  sessionId: string,
  body: QuizGradeInput,
): Promise<ManualGradeResult> {
  const res = await fetcher<ManualGradeResult>(
    `/v1/quiz-platform/sessions/${sessionId}/grade`,
    { method: "PATCH", body: JSON.stringify(body) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST /v1/quiz-platform/sessions/:sessionId/ai-grade — request AI draft grades for essays missing suggestions */
export async function requestAiEssayGrade(sessionId: string): Promise<void> {
  const res = await fetcher<unknown>(
    `/v1/quiz-platform/sessions/${sessionId}/ai-grade`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

// ── Creator analytics ───────────────────────────────────────────────────────

/** GET /v1/quiz-platform/quizzes/:id/analytics */
export async function fetchQuizAnalytics(quizId: string): Promise<QuizAnalyticsData> {
  const res = await fetcher<{ analytics: QuizAnalyticsData }>(
    `/v1/quiz-platform/quizzes/${quizId}/analytics`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.analytics;
}

/** GET /v1/quiz-platform/quizzes/:id/analytics/advanced — Exam Pro+ / Advanced Analytics add-on */
export async function fetchAdvancedQuizAnalytics(quizId: string): Promise<import("./types").AdvancedAnalyticsData> {
  const res = await fetcher<{ analytics: import("./types").AdvancedAnalyticsData }>(
    `/v1/quiz-platform/quizzes/${quizId}/analytics/advanced`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.analytics;
}

// ── Creator export ──────────────────────────────────────────────────────────

/**
 * Trigger a browser download for CSV or PDF export.
 * Uses window.open to let the browser handle the attachment header.
 */
export function triggerQuizExport(quizId: string, format: "csv" | "pdf"): void {
  const API_BASE = getApiBaseUrl();
  const url = `${API_BASE}/v1/quiz-platform/quizzes/${quizId}/export/${format}`;
  window.open(url, "_blank", "noopener,noreferrer");
}

// ── Template Gallery API (Phase 2) ─────────────────────────────────────────

/** GET /v1/quiz-templates/gallery — public template list */
export async function fetchTemplateGallery(
  params: TemplateGalleryParams,
): Promise<TemplateGalleryResponse> {
  const q = new URLSearchParams();
  if (params.category) q.set("category", params.category);
  if (params.difficulty) q.set("difficulty", params.difficulty);
  if (params.assessmentType) q.set("assessmentType", params.assessmentType);
  if (params.language) q.set("language", params.language);
  if (params.search) q.set("search", params.search);
  if (params.sort) q.set("sort", params.sort);
  if (params.page) q.set("page", String(params.page));
  if (params.limit) q.set("limit", String(params.limit));
  const qs = q.toString();
  const res = await fetcher<TemplateGalleryResponse>(
    `/v1/quiz-templates/gallery${qs ? `?${qs}` : ""}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-templates/gallery/:slug — template detail */
export async function fetchTemplateBySlug(slug: string): Promise<QuizTemplateDetail> {
  const res = await fetcher<{ template: QuizTemplateDetail }>(
    `/v1/quiz-templates/gallery/${slug}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.template;
}

/** POST /v1/quiz-templates/gallery/:id/use — duplicate template as draft */
export async function applyQuizTemplate(
  templateId: string,
): Promise<{ quizId: string; slug: string }> {
  const res = await fetcher<{ quizId: string; slug: string }>(
    `/v1/quiz-templates/gallery/${templateId}/use`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST /v1/quiz-platform/quizzes/:id/publish-template — Creator Pro+ publish as template */
export async function publishQuizAsTemplate(
  quizId: string,
  category?: string,
): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/quizzes/${quizId}/publish-template`,
    { method: "POST", body: JSON.stringify({ category }) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/** DELETE /v1/quiz-platform/quizzes/:id/unpublish-template — remove from gallery */
export async function unpublishQuizTemplate(quizId: string): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/quizzes/${quizId}/unpublish-template`,
    { method: "DELETE" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

// ── Template category constants ─────────────────────────────────────────────

export const TEMPLATE_CATEGORIES: Array<{ value: string; label: string; labelBn: string; emoji: string }> = [
  { value: "science", label: "Science", labelBn: "বিজ্ঞান", emoji: "🔬" },
  { value: "mathematics", label: "Mathematics", labelBn: "গণিত", emoji: "📐" },
  { value: "programming", label: "Programming", labelBn: "প্রোগ্রামিং", emoji: "💻" },
  { value: "language", label: "Language", labelBn: "ভাষা", emoji: "📖" },
  { value: "business", label: "Business", labelBn: "ব্যবসায়", emoji: "💼" },
  { value: "history", label: "History", labelBn: "ইতিহাস", emoji: "🏛️" },
  { value: "geography", label: "Geography", labelBn: "ভূগোল", emoji: "🌍" },
  { value: "general", label: "General Knowledge", labelBn: "সাধারণ জ্ঞান", emoji: "🧠" },
];

// ── Google Forms import ────────────────────────────────────────────────────

/** POST /v1/quiz-platform/import/google-forms — import a Google Form as a draft quiz */
export async function importFromGoogleForms(url: string): Promise<{ _id: string }> {
  const res = await fetcher<{ quiz: { _id: string } }>(
    "/v1/quiz-platform/import/google-forms",
    { method: "POST", body: JSON.stringify({ url }) },
  );
  if (!res.success) {
    const errMsg = (res as { error?: { message?: string } }).error?.message;
    throw new Error(errMsg ?? "Failed to import Google Form");
  }
  return { _id: res.data.quiz._id };
}

// ── Exam roster (EX-EXM03) ─────────────────────────────────────────────────

/** POST /v1/quizzes/exam-join — roster student joins with code + roll + credential. */
export async function joinExamRoster(input: {
  code: string;
  rollNumber: string;
  credential: string;
}): Promise<ExamJoinResponse> {
  const res = await fetcher<ExamJoinResponse>("/v1/quizzes/exam-join", {
    method: "POST",
    body: JSON.stringify(input),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-platform/quizzes/:id/roster — creator roster with credentials. */
export async function fetchExamRoster(quizId: string): Promise<ExamRosterResponse> {
  const res = await fetcher<ExamRosterResponse>(
    `/v1/quiz-platform/quizzes/${quizId}/roster`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST /v1/quiz-platform/quizzes/:id/roster/import — CSV import (server parses). */
export async function importExamRoster(
  quizId: string,
  csv: string,
): Promise<ExamRosterImportResponse> {
  const res = await fetcher<ExamRosterImportResponse>(
    `/v1/quiz-platform/quizzes/${quizId}/roster/import`,
    { method: "POST", body: JSON.stringify({ csv }) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** DELETE /v1/quiz-platform/quizzes/:id/roster/:entryId — refuse once joined. */
export async function deleteExamRosterEntry(
  quizId: string,
  entryId: string,
): Promise<void> {
  const res = await fetcher(
    `/v1/quiz-platform/quizzes/${quizId}/roster/${entryId}`,
    { method: "DELETE" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
}

/** POST /v1/quiz-platform/quizzes/:id/roster/:entryId/regenerate — new credential. */
export async function regenerateExamRosterCredential(
  quizId: string,
  entryId: string,
): Promise<ExamRosterEntryView> {
  const res = await fetcher<{ entry: ExamRosterEntryView }>(
    `/v1/quiz-platform/quizzes/${quizId}/roster/${entryId}/regenerate`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data.entry;
}

// ── Live invigilation console (EX-EXM05, docs/27 §9.4) ─────────────────────
//
// One family under /v1/quiz-platform/quizzes/:id/monitor*, behind
// `exam.invigilate` plus the server's ownership assert. Nothing here writes to
// a student's answers: the code panel is a read.

/** GET /v1/quiz-platform/quizzes/:id/monitor — the roster grid. */
export async function fetchInvigilationBoard(
  quizId: string,
  sort: InvigilationSort = "risk",
): Promise<InvigilationBoard> {
  const res = await fetcher<InvigilationBoard>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor?sort=${sort}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET .../monitor/sessions/:sessionId — one student's live panel. */
export async function fetchInvigilationStudent(
  quizId: string,
  sessionId: string,
): Promise<InvigilationStudentPanel> {
  const res = await fetcher<InvigilationStudentPanel>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor/sessions/${sessionId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET .../monitor/room (`EX-SEC03`): the projector page, the code on screen and who is waiting. */
export async function fetchExamRoom(quizId: string): Promise<ExamRoom> {
  const res = await fetcher<ExamRoom>(`/v1/quiz-platform/quizzes/${quizId}/monitor/room`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/start (`EX-SEC03`): start the exam for everyone waiting. */
export async function startExamForEveryone(quizId: string): Promise<ExamRoomStartResult> {
  const res = await fetcher<ExamRoomStartResult>(`/v1/quiz-platform/quizzes/${quizId}/monitor/start`, {
    method: "POST",
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/start/reset (`EX-SEC03b`): undo the Start, so anyone who starts from now waits for the next one. */
export async function resetExamStart(quizId: string): Promise<ExamRoomResetResult> {
  const res = await fetcher<ExamRoomResetResult>(`/v1/quiz-platform/quizzes/${quizId}/monitor/start/reset`, {
    method: "POST",
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

// ── Ask the invigilator (`EX-SEC14`, `docs/27` §9.26) ─────────────────────
// A refusal is thrown as the fetcher's `ApiError`, so the panel reads its
// `status` (429) and `code` (`EXAM_QUESTION_LIMIT`, ...) to say why.

/** GET /v1/quiz-sessions/:sessionId/questions: the student's own thread. */
export async function fetchExamQuestionThread(sessionId: string): Promise<ExamQuestionThread> {
  const res = await fetcher<ExamQuestionThread>(`/v1/quiz-sessions/${sessionId}/questions`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST /v1/quiz-sessions/:sessionId/questions: ask; answers the thread. */
export async function askExamQuestion(sessionId: string, message: string): Promise<ExamQuestionThread> {
  const res = await fetcher<ExamQuestionThread>(`/v1/quiz-sessions/${sessionId}/questions`, {
    method: "POST",
    body: JSON.stringify({ message }),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET .../monitor/questions: every question on this exam, the open ones first. */
export async function fetchMonitorExamQuestions(quizId: string): Promise<MonitorExamQuestions> {
  const res = await fetcher<MonitorExamQuestions>(`/v1/quiz-platform/quizzes/${quizId}/monitor/questions`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/sessions/:sessionId/questions/:questionId/reply: private, or to everyone as the notice. */
export async function replyToExamQuestion(
  quizId: string,
  sessionId: string,
  questionId: string,
  body: { message: string; toEveryone: boolean },
): Promise<ExamQuestionReplyResult> {
  const res = await fetcher<ExamQuestionReplyResult>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor/sessions/${sessionId}/questions/${questionId}/reply`,
    { method: "POST", body: JSON.stringify(body) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/broadcast — a message to everyone still sitting. */
export async function broadcastExamMessage(
  quizId: string,
  message: string,
): Promise<InvigilationBroadcastResult> {
  const res = await fetcher<InvigilationBroadcastResult>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor/broadcast`,
    { method: "POST", body: JSON.stringify({ message }) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/sessions/:sessionId/extra-time — minutes, server converts. */
export async function grantExamExtraTime(
  quizId: string,
  sessionId: string,
  minutes: number,
): Promise<InvigilationExtraTimeResult> {
  const res = await fetcher<InvigilationExtraTimeResult>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor/sessions/${sessionId}/extra-time`,
    { method: "POST", body: JSON.stringify({ minutes }) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/sessions/:sessionId/force-submit — end and grade a sitting. */
export async function forceSubmitExamSession(
  quizId: string,
  sessionId: string,
): Promise<InvigilationForceSubmitResult> {
  const res = await fetcher<InvigilationForceSubmitResult>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor/sessions/${sessionId}/force-submit`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../monitor/sessions/:sessionId/release — unlock a flagged student. */
export async function releaseExamSession(
  quizId: string,
  sessionId: string,
): Promise<{ sessionId: string; status: string }> {
  const res = await fetcher<{ sessionId: string; status: string }>(
    `/v1/quiz-platform/quizzes/${quizId}/monitor/sessions/${sessionId}/release`,
    { method: "POST" },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

// ── Post-exam integrity report (EX-EXM06, docs/27 §9.16) ───────────────────
//
// One family under /v1/quiz-platform/quizzes/:id/integrity*, behind the same
// `exam.invigilate` grant and the same ownership assert as the live console. Two
// reads and one write; the write is the only path that can change a coding mark,
// and it requires a stated reason.

/** GET /v1/quiz-platform/quizzes/:id/integrity — the cohort, flagged tail first. */
export async function fetchIntegrityBoard(quizId: string): Promise<IntegrityBoard> {
  const res = await fetcher<IntegrityBoard>(`/v1/quiz-platform/quizzes/${quizId}/integrity`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET .../integrity/sessions/:sessionId — one student's report. */
export async function fetchIntegrityReport(
  quizId: string,
  sessionId: string,
): Promise<IntegrityReport> {
  const res = await fetcher<IntegrityReport>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/sessions/${sessionId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/sessions/:sessionId/playback/:questionId — one task's replay.
 *
 * `EX-AUT02`. Its own request rather than a field on the report, because this is
 * the only read in the family that reconstructs a student's source: it is asked
 * for explicitly, about one named task, and never arrives because somebody opened
 * a mark sheet. A task with nothing recorded answers `404`.
 */
export async function fetchCodePlayback(
  quizId: string,
  sessionId: string,
  questionId: string,
): Promise<CodePlaybackView> {
  const res = await fetcher<CodePlaybackView>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/sessions/${sessionId}/playback/${questionId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/similarity — the cluster review.
 *
 * `EX-AUT04`. About the room rather than about one student, which is why it is
 * its own read and not a field on the board: a board row is one sitting and a
 * cluster is a group of them. Carries no source code.
 */
export async function fetchSimilarityClusters(quizId: string): Promise<ExamClusterBoard> {
  const res = await fetcher<ExamClusterBoard>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/similarity`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/similarity/:problemId/pairs/:left/:right — the side-by-side.
 *
 * `EX-AUT04`. The only read in this family that returns any part of two
 * students' source, and it returns **the matched passages only** — the server
 * never assembles a whole submission for this route.
 */
export async function fetchSimilarityPair(
  quizId: string,
  problemId: string,
  leftUserId: string,
  rightUserId: string,
): Promise<ExamSimilarityPair> {
  const res = await fetcher<ExamSimilarityPair>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/similarity/${problemId}/pairs/${leftUserId}/${rightUserId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/ai-trap: the answers carrying their sitting's AI trap word.
 *
 * `EX-SEC06`. Each row is a student, a question and a snippet around the word:
 * evidence for the teacher to read, never a verdict.
 */
export async function fetchAiTrapMarkers(quizId: string): Promise<ExamAiTrapBoard> {
  const res = await fetcher<ExamAiTrapBoard>(`/v1/quiz-platform/quizzes/${quizId}/integrity/ai-trap`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/leak-finder: what the exam's papers carry, before a search.
 *
 * `EX-SEC09`. How many wording marks a paper carries, how many papers that
 * tells apart, and two sample papers for "How it works".
 */
export async function fetchLeakFinderOverview(quizId: string): Promise<LeakFinderOverview> {
  const res = await fetcher<LeakFinderOverview>(`/v1/quiz-platform/quizzes/${quizId}/integrity/leak-finder`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * POST .../integrity/leak-finder: the pasted text, read for the wording marks.
 *
 * `EX-SEC09`. A body, so the text never sits in a URL; the server stores it
 * nowhere. Evidence for the teacher, never a verdict.
 */
export async function findLeakedPaper(quizId: string, text: string): Promise<LeakFinderResult> {
  const res = await fetcher<LeakFinderResult>(`/v1/quiz-platform/quizzes/${quizId}/integrity/leak-finder`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/answers: similar answers in the choice questions.
 *
 * `EX-SEC04`. The room: pairs and groups whose wrong answers match far more
 * than chance explains, with a per-question strip. Names and counts only; no
 * answer travels in this payload.
 */
export async function fetchAnswerSimilarity(quizId: string): Promise<ExamAnswerSimilarityBoard> {
  const res = await fetcher<ExamAnswerSimilarityBoard>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/answers`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * GET .../integrity/answers/pairs/:left/:right: two sittings question by question.
 *
 * `EX-SEC04`. Both ids must be closed sittings of this exam; anything else is
 * a 404 from the server.
 */
export async function fetchAnswerPair(
  quizId: string,
  leftSessionId: string,
  rightSessionId: string,
): Promise<ExamAnswerPair> {
  const res = await fetcher<ExamAnswerPair>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/answers/pairs/${leftSessionId}/${rightSessionId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST .../integrity/sessions/:sessionId/coding-mark — override, with a reason. */
export async function overrideCodingMark(
  quizId: string,
  sessionId: string,
  input: { questionId: string; awarded: number; reason: string },
): Promise<CodingMarkOverrideResult> {
  const res = await fetcher<CodingMarkOverrideResult>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/sessions/${sessionId}/coding-mark`,
    { method: "POST", body: JSON.stringify(input) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

// ── The decision, and the appeal (EX-AUT05, docs/27 §5.5.3, §9.21) ──────────

/**
 * POST .../integrity/sessions/:sessionId/decision — record or revise a decision.
 *
 * The only call in this client that can move a mark for an integrity reason. The
 * body has no field that could carry a risk score, and the server's schema is
 * `.strict()` so one cannot be added by accident from this side.
 */
export async function recordIntegrityDecision(
  quizId: string,
  sessionId: string,
  input: { outcome: IntegrityOutcome; rationale: string; adjustment?: number },
): Promise<{ sessionId: string; decision: IntegrityDecision }> {
  const res = await fetcher<{ sessionId: string; decision: IntegrityDecision }>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/sessions/${sessionId}/decision`,
    { method: "POST", body: JSON.stringify(input) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * POST .../integrity/sessions/:sessionId/appeal — answer an open appeal.
 *
 * Upholds the decision, in the grader's own words. Revising it instead is the
 * decision call above, which closes the appeal as `revised`.
 */
export async function respondToIntegrityAppeal(
  quizId: string,
  sessionId: string,
  input: { response: string },
): Promise<{ sessionId: string; decision: IntegrityDecision }> {
  const res = await fetcher<{ sessionId: string; decision: IntegrityDecision }>(
    `/v1/quiz-platform/quizzes/${quizId}/integrity/sessions/${sessionId}/appeal`,
    { method: "POST", body: JSON.stringify(input) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/**
 * POST /v1/quiz-sessions/:sessionId/integrity-appeal — the student's own appeal.
 *
 * The one write in this feature a student can make. Scoped server-side by the
 * session **and** the student together, so it cannot be pointed elsewhere.
 */
export async function submitIntegrityAppeal(
  sessionId: string,
  statement: string,
): Promise<StudentIntegrityDecision> {
  const res = await fetcher<StudentIntegrityDecision>(
    `/v1/quiz-sessions/${sessionId}/integrity-appeal`,
    { method: "POST", body: JSON.stringify({ statement }) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

// ── Photo proctoring (`EX-PP01`, `docs/27` §9.33) ───────────────────────────

export type EvidenceUploadTicket = {
  imageId: string;
  leaveId?: string;
  upload: { url: string; method: "PUT"; headers: Record<string, string>; expiresAt: string };
};

/** POST /v1/quiz-sessions/:sessionId/photos: sign one picture's upload to R2. */
export async function signEvidenceUpload(
  sessionId: string,
  body: { kind: "snapshot" | "leave" | "webcam"; contentType: string; sizeBytes: number; sha256: string; leaveId?: string; questionId?: string },
): Promise<EvidenceUploadTicket> {
  const res = await fetcher<EvidenceUploadTicket>(`/v1/quiz-sessions/${sessionId}/photos`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST /v1/quiz-sessions/:sessionId/photos/:imageId/done: the picture is in the bucket. */
export async function completeEvidenceUpload(sessionId: string, imageId: string): Promise<void> {
  await fetcher<{ stored: boolean }>(`/v1/quiz-sessions/${sessionId}/photos/${imageId}/done`, { method: "POST" });
}

/** POST /v1/quiz-sessions/:sessionId/photo/events: sharing or the camera on or off, or back from a leave. */
export async function sendPhotoEvent(
  sessionId: string,
  body:
    | { type: "share"; on: boolean; surface?: ShareSurface }
    | { type: "back"; leaveId: string }
    | { type: "camera"; on: boolean },
): Promise<void> {
  await fetcher<unknown>(`/v1/quiz-sessions/${sessionId}/photo/events`, { method: "POST", body: JSON.stringify(body) });
}

/**
 * `EX-PP03`: POST /v1/quizzes/:quizId/phone-pairings, a QR for the phone.
 * Before the start for this exam; with `sessionId`, a reconnect mid-exam.
 */
export async function createPhonePairing(quizId: string, sessionId?: string): Promise<PhonePairingTicket> {
  const res = await fetcher<PhonePairingTicket>(`/v1/quizzes/${quizId}/phone-pairings`, {
    method: "POST",
    body: JSON.stringify(sessionId ? { sessionId } : {}),
  });
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** `EX-PP03`: GET /v1/quizzes/:quizId/phone-pairings/:pairingId, the QR card's poll. */
export async function fetchPhonePairing(quizId: string, pairingId: string): Promise<PhonePairingStatus> {
  const res = await fetcher<PhonePairingStatus>(`/v1/quizzes/${quizId}/phone-pairings/${pairingId}`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET .../integrity/sessions/:sessionId/photos: one sitting's pictures, for the owner. */
export async function fetchSittingEvidence(quizId: string, sessionId: string): Promise<SittingEvidence> {
  const res = await fetcher<SittingEvidence>(`/v1/quiz-platform/quizzes/${quizId}/integrity/sessions/${sessionId}/photos`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-platform/photo-credits: the teacher's credits, and a quiz's roster for the forecast. */
export async function fetchPhotoCredits(quizId?: string): Promise<PhotoCreditSummary> {
  const res = await fetcher<PhotoCreditSummary>(
    `/v1/quiz-platform/photo-credits${quizId ? `?quizId=${encodeURIComponent(quizId)}` : ""}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** GET /v1/quiz-platform/photo-credits/ledger: another page of the ledger. */
export async function fetchPhotoCreditLedger(page: number): Promise<PhotoCreditSummary["ledger"]> {
  const res = await fetcher<PhotoCreditSummary["ledger"]>(`/v1/quiz-platform/photo-credits/ledger?page=${page}`);
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}
